import {DateTime} from 'luxon';
import {exists} from 'shared/utils/custom-utils';

export const INCLUSIVE_START_BRACKET = '[';
export const EXCLUSIVE_START_BRACKET = '(';
export const INCLUSIVE_END_BRACKET = ']';
export const EXCLUSIVE_END_BRACKET = ')';
export const NEGATIVE_INFINITY = '-infinity';
export const POSITIVE_INFINITY = 'infinity';
export const EMPTY_INTERVAL = 'empty';

type InclusiveStartBracket = typeof INCLUSIVE_START_BRACKET;
type ExclusiveStartBracket = typeof EXCLUSIVE_START_BRACKET;
type InclusiveEndBracket = typeof INCLUSIVE_END_BRACKET;
type ExclusiveEndBracket = typeof EXCLUSIVE_END_BRACKET;
type IntervalStartBracket = InclusiveStartBracket | ExclusiveStartBracket;
type IntervalEndBracket = InclusiveEndBracket | ExclusiveEndBracket;

type BoundPosition = 'start' | 'end';

type DateTimeWithZoneString = string & {readonly __brand: 'DateTimeWithZoneString'};
type QuotedDateTimeWithZoneString = `"${DateTimeWithZoneString}"`;
type IntervalStartDate = QuotedDateTimeWithZoneString | typeof NEGATIVE_INFINITY;
type IntervalEndDate = QuotedDateTimeWithZoneString | typeof POSITIVE_INFINITY;

export type EmptyIntervalString = typeof EMPTY_INTERVAL;

// Examples of IntervalString:
//   '["2021-01-01T00:00:00Z","2021-01-02T00:00:00Z")'
//   '["2021-01-01T00:00:00Z",infinity]'
//   '[-infinity,"2021-01-02T00:00:00Z")'
//   'empty'
export type IntervalString =
    EmptyIntervalString
    | `${IntervalStartBracket}${IntervalStartDate},${IntervalEndDate}${IntervalEndBracket}`;

// Both from and to undefined = empty interval
// Examples of DateRangeInterval:
//   {from: DateTime.fromISO('2021-01-01T00:00:00Z'), to: DateTime.fromISO('2021-01-02T10:00:00+02:00')}
//   {from: DateTime.fromISO('2021-01-01T00:00:00Z'), to: POSITIVE_INFINITY}
//   {from: NEGATIVE_INFINITY, to: DateTime.fromISO('2021-01-02T00:00:00Z')}
//   It is always inclusive
export interface DateRangeInterval {
    from?: DateTime | typeof NEGATIVE_INFINITY;
    to?: DateTime | typeof POSITIVE_INFINITY;
}

// TODO: vzít časovou zonu ze systému
export function parseIntervalStringToObject(interval: IntervalString | null | undefined): DateRangeInterval {
    if (!exists(interval) || interval.trim() === '' || interval === EMPTY_INTERVAL) {
        return {};
    }

    const startBracket = interval[0] as IntervalStartBracket;
    const endBracket = interval[interval.length - 1] as IntervalEndBracket;

    // Validate brackets
    if (![INCLUSIVE_START_BRACKET, EXCLUSIVE_START_BRACKET].includes(startBracket)) {
        throw new Error(`Invalid start bracket: ${startBracket}`);
    }
    if (![INCLUSIVE_END_BRACKET, EXCLUSIVE_END_BRACKET].includes(endBracket)) {
        throw new Error(`Invalid end bracket: ${endBracket}`);
    }

    const inside = interval.substring(1, interval.length - 1);
    const commaIndex = inside.indexOf(',');

    if (commaIndex < 0) {
        throw new Error(`Invalid interval string (missing comma): ${interval}`);
    }

    const leftPart = inside.substring(0, commaIndex).trim();
    const rightPart = inside.substring(commaIndex + 1).trim();

    const startDate = parseIntervalBound(leftPart, 'start');
    const endDate = parseIntervalBound(rightPart, 'end');

    let adjustedStartDate = startDate;
    let adjustedEndDate = endDate;

    // Handle start-exclusive bound by adding by 1 millisecond
    if (startBracket === EXCLUSIVE_START_BRACKET && startDate !== NEGATIVE_INFINITY && startDate !== null) {
        adjustedStartDate = startDate.plus({milliseconds: 1});
    }

    // Handle end-exclusive bound by subtracting 1 millisecond
    if (endBracket === EXCLUSIVE_END_BRACKET && endDate !== POSITIVE_INFINITY && endDate !== null) {
        adjustedEndDate = endDate.minus({milliseconds: 1});
    }

    return {
        from: adjustedStartDate,
        to: adjustedEndDate
    };
}

// Always inclusive
// Always in UTC
// Examples of output:
//   '["2021-01-01T00:00:00.000Z","2021-01-02T00:00:00.000Z"]'
//   '["2021-01-01T00:00:00.000Z",infinity]'
//   '[-infinity,"2021-01-02T00:00:00.000Z"]'
//   'empty'
export function parseObjectToIntervalString(interval: DateRangeInterval | null | undefined): IntervalString {
    if (!exists(interval) || (!exists(interval.from) && !exists(interval.to))) {
        return EMPTY_INTERVAL;
    }

    // Check if an interval is invalid (to < from)
    if (isIntervalInvalid(interval)) {
        return EMPTY_INTERVAL; // Invalid interval - treat as empty
    }

    // Handle the same date case: expand to full day (start of day to end of day)
    if (exists(interval.from) && exists(interval.to) &&
        DateTime.isDateTime(interval.from) && DateTime.isDateTime(interval.to) &&
        interval.from.toUTC().hasSame(interval.to.toUTC(), 'day')) {

        const startOfDay = interval.from.toUTC().startOf('day');
        const endOfDay = interval.from.toUTC().endOf('day');

        const start = stringifyDateTime(startOfDay);
        const end = stringifyDateTime(endOfDay);

        return `${INCLUSIVE_START_BRACKET}${start},${end}${INCLUSIVE_END_BRACKET}` as IntervalString;
    }

    const startBracket: InclusiveStartBracket = INCLUSIVE_START_BRACKET;
    const endBracket: InclusiveEndBracket = INCLUSIVE_END_BRACKET;

    let startString: IntervalStartDate;
    if (!exists(interval.from) || interval.from === NEGATIVE_INFINITY) {
        startString = NEGATIVE_INFINITY;
    } else {
        startString = stringifyDateTime(interval.from);
    }

    let endString: IntervalEndDate;
    if (!exists(interval.to) || interval.to === POSITIVE_INFINITY) {
        endString = POSITIVE_INFINITY;
    } else {
        endString = stringifyDateTime(interval.to);
    }

    return `${startBracket}${startString},${endString}${endBracket}` as IntervalString;
}

export function areIntervalsEqual(a: DateRangeInterval, b: DateRangeInterval): boolean {
    const equalFrom = () => {
        if (exists(a.from) && exists(b.from) && DateTime.isDateTime(a.from) && DateTime.isDateTime(b.from)) {
            return areDateTimesEqual(a.from, b.from);
        }

        return a.from === b.from;
    };

    const equalTo = () => {
        if (exists(a.to) && exists(b.to) && DateTime.isDateTime(a.to) && DateTime.isDateTime(b.to)) {
            return areDateTimesEqual(a.to, b.to);
        }

        return a.to === b.to;
    };

    return equalFrom() && equalTo();
}

export function isIntervalInvalid(interval: DateRangeInterval): boolean {
    // Handle null/undefined intervals
    if (!exists(interval)) {
        return false;
    }

    // An interval is invalid if both bounds exist as DateTime objects and to < from
    return exists(interval.from) && exists(interval.to) &&
        DateTime.isDateTime(interval.from) && DateTime.isDateTime(interval.to) &&
        interval.to < interval.from;
}

export function isIntervalStringValid(intervalString: string): intervalString is IntervalString {
    try {
        parseIntervalStringToObject(intervalString as IntervalString);
        return true;
    } catch {
        return false;
    }
}

export function isDateInInterval(date: DateTime, interval: DateRangeInterval): boolean {
    if (!DateTime.isDateTime(date) || !date.isValid) {
        throw new Error('Invalid DateTime provided');
    }

    if (!exists(interval.from) || !exists(interval.to)) {
        return false;
    }

    // Check if an interval is invalid (to < from)
    if (exists(interval.from) && exists(interval.to) && DateTime.isDateTime(interval.from) && DateTime.isDateTime(interval.to) && interval.to < interval.from) {
        return false; // Invalid interval - treat as empty
    }

    if (exists(interval.from)) {
        if (interval.from === NEGATIVE_INFINITY) {
            // No lower bound restriction
        } else if (DateTime.isDateTime(interval.from) && date < interval.from) {
            return false;
        }
    }

    if (exists(interval.to)) {
        if (interval.to === POSITIVE_INFINITY) {
            // No upper bound restriction
        } else if (DateTime.isDateTime(interval.to) && date > interval.to) {
            return false;
        }
    }

    return true;
}

function parseIntervalBound(boundString: string, position: 'start'): DateTime | typeof NEGATIVE_INFINITY | null;
function parseIntervalBound(boundString: string, position: 'end'): DateTime | typeof POSITIVE_INFINITY | null;

function parseIntervalBound(boundString: string, position: BoundPosition): DateTime | typeof NEGATIVE_INFINITY | typeof POSITIVE_INFINITY | null {
    if (!boundString || boundString === NEGATIVE_INFINITY) {
        return position === 'start' ? NEGATIVE_INFINITY : null;
    }

    if (boundString === POSITIVE_INFINITY) {
        return position === 'start' ? null : POSITIVE_INFINITY;
    }

    const unquoted = unquoteString(boundString);
    const parsed = DateTime.fromISO(unquoted).toUTC();

    if (!parsed.isValid) {
        throw new Error(`Invalid date format: ${unquoted}`);
    }

    return parsed;
}

function unquoteString(str: string): string {
    if (str.length >= 2 && str.charAt(0) === '"' && str.charAt(str.length - 1) === '"') {
        return str.substring(1, str.length - 1);
    }

    return str;
}

// Same moments in different timezones are considered equal
function areDateTimesEqual(a: DateTime, b: DateTime): boolean {
    return a.toUTC().toISO() === b.toUTC().toISO();
}

// Always in UTC and quoted
function stringifyDateTime(dateTime: DateTime): QuotedDateTimeWithZoneString {
    return `"${dateTime.toUTC().toISO() as DateTimeWithZoneString}"`;
}